/**
 * Formats a date string according to specified format and locale.
 * 
 * @param {string|Date} dateString - The date to format. Can be an ISO 8601 string or Date object.
 * @param {string|Function} [format='toLocaleString'] - The format to use. Can be:
 *   - A format string with tokens (e.g., 'YYYY-MM-dd HH:mm:ss')
 *   - A predefined format name (see KNOWN_FORMATS)
 *   - A formatting function that receives the Date object
 * @param {string} [locale='default'] - The BCP 47 language tag for localization
 * @returns {string} Formatted date string or 'N/A'/'Invalid Date' for invalid input
 * 
 * @example
 * // Using format strings
 * formatDate('2025-06-13T12:00:00Z', 'YYYY-MM-dd HH:mm:ss') // "2025-06-13 12:00:00"
 * formatDate('2025-06-13', 'dd/MM/YYYY') // "13/06/2025"
 * 
 * // Using predefined formats
 * formatDate('2025-06-13', 'dayMonth') // "13 June"
 * formatDate('2025-06-13', 'weekdayMonthDay') // "Friday, Jun 13"
 * 
 * // Using formatting function
 * formatDate('2025-06-13', d => `Year: ${d.getFullYear()}`) // "Year: 2025"
 * 
 * // Localization
 * formatDate('2025-06-13', 'dayMonth', 'fr-FR') // "13 juin"
 */
const formatDate = (dateString: string | Date, format: string | Function = 'toLocaleString', locale: string = 'default'): string => {
  if (!dateString) return 'N/A';

  let date;
  try {
    date = new Date(dateString);
  } catch (e) {
    console.error('Invalid date string:', dateString);
    return 'Invalid Date';
  }


  if (date.toString() === 'Invalid Date') {
    return 'Invalid Date';
  }

  if (typeof format === 'function') {
    return format(date);
  }

  const knownFormats = [
    'toLocaleString',
    'toLocaleDateString',
    'toLocaleTimeString',
    'time',
    'datetime',
    'date',
    'dayMonth',
    'day',
    'month',
    'year',
    'weekdayMonthDay',
    'iso'
  ];

  if (!knownFormats.includes(format) && /[YMdHymMs]/.test(format)) {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const map: { [key: string]: string | number } = {
      YYYY: date.getFullYear(),
      YY: date.getFullYear().toString().slice(-2),
      MM: pad(date.getMonth() + 1),
      dd: pad(date.getDate()),
      HH: pad(date.getUTCHours()),
      mm: pad(date.getUTCMinutes()),
      ss: pad(date.getUTCSeconds()),
      d: date.getDate(),
      M: date.getMonth() + 1,
      Y: date.getFullYear(),
      y: date.getFullYear().toString().slice(-2)
    };

    return format.replace(/YYYY|YY|MM|dd|HH|mm|ss|d|M|Y|y/g, (match) => {
      return String(map[match]);
    });
  }

  switch (format) {
    case 'toLocaleString':
      return date.toLocaleString(locale);
    case 'toLocaleDateString':
      return date.toLocaleDateString(locale);
    case 'toLocaleTimeString':
      return date.toLocaleTimeString(locale);
    // Friendly, locale-aware presets for template authors (no Intl options or
    // raw Date needed). 'time' is hour:minute with no seconds.
    case 'time':
      return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
    case 'datetime':
      return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
    case 'date':
      return date.toLocaleDateString(locale, { dateStyle: 'medium' });
    case 'day':
      return date.toLocaleDateString(locale, { weekday: 'long' });
    case 'dayMonth':
      return date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    case 'month':
      return date.toLocaleDateString(locale, { month: 'long' });
    case 'year':
      return date.toLocaleDateString(locale, { year: 'numeric' });
    case 'weekdayMonthDay':
      return date.toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' });
    case 'iso':
      return date.toISOString();
    default:
      return `Format '${format}' is not supported. Please use a valid format string or Date method.`;
  }
};

// Example usage:
// formatDate('2025-06-12T12:31:52Z', 'dayMonth') -> "12 June"
// formatDate('2025-06-12T12:31:52Z', (d) => d.getFullYear()) -> "2025"
// formatDate('2025-06-12T12:31:52Z', 'YY:dd') -> "25:12"


export default formatDate;
